// src/pages/AcceptInvite.jsx
// Public landing page for invitation links. Resolves the token, presents the
// invite metadata, and gates acceptance behind login or registration with the
// invited email address.

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authAPI, orgAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { CheckCircle2, AlertTriangle, LogOut, UserPlus, LogIn, Loader2 } from 'lucide-react';

const Card = ({ children }) => (
  <div style={{
    maxWidth: 480, margin: '60px auto', padding: 28,
    background: 'white', border: '1px solid var(--border)', borderRadius: 8,
    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
  }}>
    {children}
  </div>
);

const Header = ({ invite }) => (
  <>
    <h1 style={{ margin: '0 0 6px', fontSize: 22, color: '#0e4e95' }}>You're invited</h1>
    <p style={{ margin: '0 0 20px', color: '#555', fontSize: 14 }}>
      <strong>{invite.organisation?.name}</strong> has invited you to join the
      profile <strong>{invite.groupProfile?.profileName}</strong> as a{' '}
      <span style={{ fontWeight: 700, color: '#0e4e95' }}>{invite.role}</span>.
    </p>
    <p style={{ margin: '0 0 18px', color: '#555', fontSize: 13 }}>
      Invited email: <code style={{ background: '#f4f4f4', padding: '2px 6px', borderRadius: 4 }}>{invite.email}</code>
    </p>
  </>
);

export const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, login, logout } = useAuth();

  const [loading, setLoading]   = useState(true);
  const [invite, setInvite]     = useState(null);
  const [error, setError]       = useState(null);
  const [accepting, setAccepting] = useState(false);

  // Tabs for unauthenticated state
  const [tab, setTab] = useState('signup');
  const [form, setForm] = useState({ email: '', password: '', firstName: '', lastName: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await orgAPI.getInviteByToken(token);
        if (!alive) return;
        setInvite(data?.data?.invite || null);
        setForm((f) => ({ ...f, email: data?.data?.invite?.email || '' }));
      } catch (err) {
        setError(err?.response?.data?.message || 'Invite not found');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const acceptNow = async () => {
    setAccepting(true);
    try {
      await orgAPI.acceptInviteByToken(token);
      toast.success('Invite accepted — welcome!');
      navigate('/select-destination', { replace: true });
    } catch {
      // interceptor toasts
    } finally {
      setAccepting(false);
    }
  };

  const handleSignupAndAccept = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await authAPI.register({
        email: invite.email,
        password: form.password,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
      });
      await login(invite.email, form.password);
      await orgAPI.acceptInviteByToken(token);
      toast.success('Account created — welcome!');
      navigate('/select-destination', { replace: true });
    } catch {
      // interceptor
    } finally {
      setSubmitting(false);
    }
  };

  const handleLoginAndAccept = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(invite.email, form.password);
      await orgAPI.acceptInviteByToken(token);
      toast.success('Invite accepted — welcome!');
      navigate('/select-destination', { replace: true });
    } catch {
      // interceptor
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#666' }}>
          <Loader2 size={18} className="spin" /> Looking up your invitation…
        </div>
      </Card>
    );
  }

  if (error || !invite) {
    return (
      <Card>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <AlertTriangle size={22} color="#CC0000" />
          <div>
            <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Invitation unavailable</h2>
            <p style={{ margin: 0, color: '#555', fontSize: 14 }}>
              {error || 'This invite link is no longer valid. Ask the admin to send you a new one.'}
            </p>
            <Link to="/" style={{ display: 'inline-block', marginTop: 14, color: '#3D80CC' }}>← Back to Tikhet</Link>
          </div>
        </div>
      </Card>
    );
  }

  if (invite.expired) {
    return (
      <Card>
        <Header invite={invite} />
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start',
          background: '#FFF7E6', border: '1px solid #E6A817', borderRadius: 6, padding: 12 }}>
          <AlertTriangle size={18} color="#E6A817" />
          <div style={{ fontSize: 13, color: '#553' }}>
            This invite link has expired. Ask the inviting admin to resend it from the
            Members page.
          </div>
        </div>
      </Card>
    );
  }

  if (invite.status === 'ACCEPTED') {
    return (
      <Card>
        <Header invite={invite} />
        <div style={{ display: 'flex', gap: 10, alignItems: 'center',
          background: '#E8F5EE', border: '1px solid #28A745', borderRadius: 6, padding: 12 }}>
          <CheckCircle2 size={18} color="#28A745" />
          <div style={{ fontSize: 13, color: '#234' }}>
            This invite has already been accepted. <Link to="/select-destination">Continue to Tikhet →</Link>
          </div>
        </div>
      </Card>
    );
  }

  // Logged-in cases
  if (user) {
    const matches = (user.email || '').toLowerCase() === (invite.email || '').toLowerCase();
    if (matches) {
      return (
        <Card>
          <Header invite={invite} />
          <button className="btn btn-success" onClick={acceptNow} disabled={accepting}
            style={{ width: '100%', padding: '12px 16px', fontSize: 14 }}>
            {accepting ? 'Accepting…' : 'Accept invitation'}
          </button>
        </Card>
      );
    }
    return (
      <Card>
        <Header invite={invite} />
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start',
          background: '#FDECEC', border: '1px solid #CC0000', borderRadius: 6, padding: 12, marginBottom: 14 }}>
          <AlertTriangle size={18} color="#CC0000" />
          <div style={{ fontSize: 13, color: '#553' }}>
            You're logged in as <strong>{user.email}</strong>, but this invite is for{' '}
            <strong>{invite.email}</strong>. Log out and sign in with the invited email to accept.
          </div>
        </div>
        <button className="btn btn-secondary" onClick={async () => { await logout(); }}
          style={{ width: '100%' }}>
          <LogOut size={14} style={{ marginRight: 6 }} /> Log out
        </button>
      </Card>
    );
  }

  // Unauthenticated: tabs for signup vs login
  return (
    <Card>
      <Header invite={invite} />

      <div style={{ display: 'flex', gap: 0, marginBottom: 14, borderBottom: '1px solid #ddd' }}>
        {[
          { key: 'signup', label: 'Create account', icon: UserPlus },
          { key: 'login',  label: 'I have an account', icon: LogIn },
        ].map(t => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)}
            style={{
              flex: 1, padding: '8px 10px', background: 'transparent',
              border: 'none', borderBottom: tab === t.key ? '2px solid #0e4e95' : '2px solid transparent',
              color: tab === t.key ? '#0e4e95' : '#666',
              fontWeight: tab === t.key ? 700 : 500, cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'signup' ? (
        <form onSubmit={handleSignupAndAccept} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <label className="form-label">Email</label>
            <input className="form-input" value={invite.email} readOnly
              style={{ background: '#f4f4f4', color: '#666' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label className="form-label">First name</label>
              <input className="form-input" value={form.firstName} required
                onChange={(e) => setForm(f => ({ ...f, firstName: e.target.value }))} />
            </div>
            <div>
              <label className="form-label">Last name</label>
              <input className="form-input" value={form.lastName} required
                onChange={(e) => setForm(f => ({ ...f, lastName: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="form-label">Password</label>
            <input className="form-input" type="password" value={form.password} required
              minLength={8}
              onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder="At least 8 characters" />
          </div>
          <button type="submit" className="btn btn-primary" disabled={submitting}
            style={{ marginTop: 6, padding: '10px 16px' }}>
            {submitting ? 'Creating account…' : 'Create account & accept'}
          </button>
        </form>
      ) : (
        <form onSubmit={handleLoginAndAccept} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <label className="form-label">Email</label>
            <input className="form-input" value={invite.email} readOnly
              style={{ background: '#f4f4f4', color: '#666' }} />
          </div>
          <div>
            <label className="form-label">Password</label>
            <input className="form-input" type="password" value={form.password} required
              onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} />
          </div>
          <button type="submit" className="btn btn-primary" disabled={submitting}
            style={{ marginTop: 6, padding: '10px 16px' }}>
            {submitting ? 'Signing in…' : 'Sign in & accept'}
          </button>
        </form>
      )}
    </Card>
  );
};
